import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CoordTransform } from './coords';
import { loadTerrainData, disposeTerrain, disposeRim, applyShading, updateRimExaggeration } from './terrain';
import { buildTileSystem, addTileSystemToScene, removeTileSystemFromScene,
         updateTileLOD, disposeTileSystem, setLodDebugOverlay } from './tiles';
import { loadAndPlacePOIs, clearMarkers, rescaleMarkers, setupInteraction,
         getMarkers, setPoisVisible, filterByCategory } from './poi';
import { loadAndPlaceCities, clearCityLabels, rescaleCityLabels,
         setCitiesVisible, toggleCitySize, updateCityLOD,
         setLabelOffset, setVisRange, setVisHierarchy, setSizeHierarchy,
         LABEL_SCALE_KEY, LABEL_OFFSET_KEY,
         VIS_RANGE_KEY, VIS_HIER_KEY, SIZE_HIER_KEY } from './cities';
import { initRegionBars, loadRegions } from './regions';
import { ARController } from './ar';
import { t, getLang, setLang, onLangChange, detectLang } from './translations';
import { loadCategories, getCategories, getCategory } from './categories';
import type { RegionData, TileSystem, MetaSettings, TerrainMeta } from './types';

const loadingEl  = document.getElementById('loading')!;
const loadingMsg = document.getElementById('loading-msg')!;

const EXAG_MIN  = 0.5;
const EXAG_MAX  = 8.0;
const EXAG_STEP = 0.25;
const EXAG_KEY  = 'ar_map_exaggeration';

let exaggeration = parseFloat(localStorage.getItem(EXAG_KEY) ?? '1.0');
exaggeration = Math.max(EXAG_MIN, Math.min(EXAG_MAX, exaggeration));

const exagSlider   = document.getElementById('exag-slider') as HTMLInputElement;
const exagLabel    = document.getElementById('exag-label')!;
const normalToggle = document.getElementById('normalmap-toggle')!;

const NM_KEY = 'ar_map_normalmap';
let normalMapEnabled = localStorage.getItem(NM_KEY) !== 'false';

const SHADING_KEY  = 'ar_map_shading';
const VALID_SHADING_MODES = new Set(['color', 'relief', 'satellite']);
function sanitizeShading(mode: string | null | undefined): string {
    return mode && VALID_SHADING_MODES.has(mode) ? mode : 'color';
}
let currentShading = sanitizeShading(localStorage.getItem(SHADING_KEY));

const LOD_DIST_KEY = 'ar_map_lod_dist';
let lodDistance = parseFloat(localStorage.getItem(LOD_DIST_KEY) ?? '30');

const LOD_DEBUG_KEY = 'ar_map_lod_debug';
let lodDebugEnabled = localStorage.getItem(LOD_DEBUG_KEY) === 'true';

let labelScale    = parseFloat(localStorage.getItem(LABEL_SCALE_KEY)  ?? '1.0');
let labelOffset   = parseFloat(localStorage.getItem(LABEL_OFFSET_KEY) ?? '0.5');
let visRange      = parseFloat(localStorage.getItem(VIS_RANGE_KEY)    ?? '75');
let visHierarchy  = parseFloat(localStorage.getItem(VIS_HIER_KEY)     ?? '1.0');
let sizeHierarchy = parseFloat(localStorage.getItem(SIZE_HIER_KEY)    ?? '1.0');

function updateShadingUI(mode: string): void {
    document.querySelectorAll<HTMLButtonElement>('.shade-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.mode === mode);
    });
}

function applyTranslations(): void {
    document.querySelectorAll<HTMLElement>('[data-i18n]').forEach(el => {
        el.textContent = t(el.dataset.i18n!);
    });
    // data-i18n-html: same as data-i18n but sets innerHTML — for content that
    // needs structure (e.g. the help modal's <li> lists). Values are
    // author-controlled translation strings, never user input.
    document.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach(el => {
        el.innerHTML = t(el.dataset.i18nHtml!);
    });
    normalToggle.textContent = normalMapEnabled ? t('on') : t('off');
    const arNormalToggle = document.getElementById('ar-normalmap-toggle');
    if (arNormalToggle) arNormalToggle.textContent = normalMapEnabled ? t('on') : t('off');
    const sl = document.getElementById('save-view-label');
    if (sl) sl.textContent = t('saveView');
    const ssl = document.getElementById('save-settings-label');
    if (ssl) ssl.textContent = t('saveSettings');
    const langBtn = document.getElementById('lang-btn');
    if (langBtn) langBtn.textContent = getLang().toUpperCase();
    const arLangBtn = document.getElementById('ar-lang-btn');
    if (arLangBtn) arLangBtn.textContent = getLang().toUpperCase();
    const pt = document.getElementById('poi-toggle');
    if (pt) pt.textContent = pt.getAttribute('aria-checked') === 'true' ? t('on') : t('off');
    const ct = document.getElementById('city-toggle');
    if (ct) ct.textContent = ct.getAttribute('aria-checked') === 'true' ? t('on') : t('off');
    const apt = document.getElementById('ar-poi-toggle');
    if (apt) apt.textContent = apt.getAttribute('aria-checked') === 'true' ? t('on') : t('off');
    const act = document.getElementById('ar-city-toggle');
    if (act) act.textContent = act.getAttribute('aria-checked') === 'true' ? t('on') : t('off');
}

function applyNormalMapEnabled(value: boolean): void {
    normalMapEnabled = value;
    const label = normalMapEnabled ? t('on') : t('off');
    normalToggle.textContent = label;
    normalToggle.setAttribute('aria-checked', String(normalMapEnabled));
    const arNormalToggle = document.getElementById('ar-normalmap-toggle');
    if (arNormalToggle) {
        arNormalToggle.textContent = label;
        arNormalToggle.setAttribute('aria-checked', String(normalMapEnabled));
    }
}

function applyNormalMapToggle(terrain: THREE.Mesh | null): void {
    applyNormalMapEnabled(normalMapEnabled);
    if (!terrain) return;
    const mat = terrain.material as THREE.MeshStandardMaterial;
    mat.normalMap = normalMapEnabled ? (terrain.userData['normalTex'] as THREE.Texture) : null;
    mat.needsUpdate = true;
}
applyNormalMapToggle(null);

function setExaggeration(val: number, terrain: THREE.Mesh | null, coords: CoordTransform | null, persist = true): void {
    exaggeration = Math.max(EXAG_MIN, Math.min(EXAG_MAX, Math.round(val / EXAG_STEP) * EXAG_STEP));
    if (persist) localStorage.setItem(EXAG_KEY, String(exaggeration));
    exagSlider.value = String(exaggeration);
    const exagText = exaggeration.toFixed(2).replace(/\.?0+$/, '') + '×';
    exagLabel.textContent = exagText;
    const arExagLabel = document.getElementById('ar-exag-ar-label');
    if (arExagLabel) arExagLabel.textContent = exagText;
    if (terrain) {
        terrain.scale.y = exaggeration;
        const mat = terrain.material as THREE.MeshStandardMaterial;
        const ns = (terrain.userData['normalScale'] as number) * exaggeration;
        mat.normalScale.set(ns, ns);
    }
    if (coords) { coords.exaggeration = exaggeration; rescaleMarkers(exaggeration); }
}

exagSlider.min  = String(EXAG_MIN);
exagSlider.max  = String(EXAG_MAX);
exagSlider.step = String(EXAG_STEP);
setExaggeration(exaggeration, null, null);

function setLoading(msg: string): void {
    loadingMsg.textContent = msg;
    loadingEl.classList.remove('hidden');
}
function hideLoading(): void {
    loadingEl.classList.add('hidden');
}

async function init(): Promise<void> {
    await detectLang();
    applyTranslations();
    onLangChange(() => applyTranslations());

    const langBtn = document.getElementById('lang-btn');
    if (langBtn) {
        langBtn.addEventListener('click', () => {
            setLang(getLang() === 'cs' ? 'en' : 'cs');
        });
    }
    const arLangBtn = document.getElementById('ar-lang-btn');
    if (arLangBtn) {
        arLangBtn.addEventListener('click', () => {
            setLang(getLang() === 'cs' ? 'en' : 'cs');
        });
    }

    setLoading(t('loading'));

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.xr.enabled = true;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type    = THREE.PCFSoftShadowMap;
    renderer.toneMapping       = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    document.getElementById('canvas-container')!.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x7aaecc);

    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.01, 600);
    camera.position.set(0, 30, 80);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.06;
    controls.maxPolarAngle = Math.PI / 2.05;

    scene.add(new THREE.AmbientLight(0xfff0e0, 0.55));
    const sun = new THREE.DirectionalLight(0xfff5d6, 1.5);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    scene.add(sun);

    // Single group that holds terrain, rim, tiles, POIs, and city labels.
    // Desktop: identity transform. AR: repositioned and scaled at hit point.
    const worldRoot = new THREE.Group();
    scene.add(worldRoot);

    let currentTerrain:    THREE.Mesh | null   = null;
    let currentCoords:     CoordTransform | null = null;
    let currentRim:        THREE.Mesh | null   = null;
    let currentSlug:       string | null       = null;
    let currentTileSystem: TileSystem | null   = null;
    let currentMeta:       TerrainMeta | null  = null;

    // Raw mesh data needed to build the tile system, captured on region load but
    // not turned into a TileSystem until satellite mode is actually requested —
    // building it eagerly on every region switch wastes GPU memory/main-thread
    // work for users who never open Satellite mode.
    let pendingTileData: {
        rawPositions: Float32Array;
        rawUVs:       Float32Array;
        segX:         number;
        segZ:         number;
        meta:         TerrainMeta;
        alphaMap:     THREE.Texture;
        slug:         string;
    } | null = null;

    function ensureTileSystem(): TileSystem | null {
        if (currentTileSystem) return currentTileSystem;
        if (!pendingTileData) return null;
        const { rawPositions, rawUVs, segX, segZ, meta, alphaMap, slug } = pendingTileData;
        currentTileSystem = buildTileSystem(rawPositions, rawUVs, segX, segZ, meta, alphaMap, slug);
        currentTileSystem.group.scale.y = exaggeration;
        if (lodDebugEnabled) setLodDebugOverlay(currentTileSystem, true);
        return currentTileSystem;
    }

    // ── WASD keyboard fly-through ─────────────────────────────────────────────
    const heldKeys = new Set<string>();
    const _up    = new THREE.Vector3(0, 1, 0);
    const _dir   = new THREE.Vector3();
    const _right = new THREE.Vector3();
    const _delta = new THREE.Vector3();

    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if ((e.target as HTMLElement).tagName === 'INPUT' ||
            (e.target as HTMLElement).tagName === 'TEXTAREA') return;
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(e.code)) {
            heldKeys.add(e.code);
            e.preventDefault();
        }
    });
    document.addEventListener('keyup',  (e: KeyboardEvent) => heldKeys.delete(e.code));
    window.addEventListener('blur',     () => heldKeys.clear());

    function applyKeyMovement(): void {
        if (heldKeys.size === 0 || arController.active || !currentTerrain) return;

        camera.getWorldDirection(_dir);
        _dir.y = 0;
        if (_dir.lengthSq() < 0.0001) return;

        _dir.normalize();
        _right.crossVectors(_dir, _up).normalize();

        const dist  = Math.max(1, camera.position.distanceTo(controls.target));
        const speed = dist * 0.015;

        _delta.set(0, 0, 0);
        if (heldKeys.has('KeyW')) _delta.addScaledVector(_dir,    speed);
        if (heldKeys.has('KeyS')) _delta.addScaledVector(_dir,   -speed);
        if (heldKeys.has('KeyA')) _delta.addScaledVector(_right, -speed);
        if (heldKeys.has('KeyD')) _delta.addScaledVector(_right,  speed);

        camera.position.add(_delta);
        controls.target.add(_delta);

        if (currentMeta) {
            const limX = currentMeta.world.width  * 2;
            const limZ = currentMeta.world.height * 2;
            const clampX = (v: number) => Math.min(limX, Math.max(-limX, v));
            const clampZ = (v: number) => Math.min(limZ, Math.max(-limZ, v));
            const clampedX = clampX(controls.target.x);
            const clampedZ = clampZ(controls.target.z);
            const overX = clampedX - controls.target.x;
            const overZ = clampedZ - controls.target.z;
            if (overX !== 0 || overZ !== 0) {
                camera.position.x += overX;
                camera.position.z += overZ;
                controls.target.x = clampedX;
                controls.target.z = clampedZ;
            }
        }
    }

    // ── AR controller ─────────────────────────────────────────────────────────
    const arController = new ARController(renderer, scene);
    const arBtn = document.getElementById('ar-btn')!;
    let arSupported = false;
    ARController.isSupported().then(ok => {
        arSupported = ok;
        arBtn.classList.toggle('ar-unsupported', !ok);
    });
    arBtn.addEventListener('click', () => {
        arBtn.classList.remove('ar-resume-pulse');
        if (!arSupported) {
            alert(t(/iP(hone|ad|od)/.test(navigator.userAgent) ? 'arUnsupportedIOS' : 'arUnsupported'));
            return;
        }
        if (!currentTerrain) return;
        arController.enter(worldRoot, controls, scene.background as THREE.Color);
    });
    document.getElementById('ar-replace-btn')!.addEventListener('click', () => arController.rePlace());
    document.getElementById('ar-exit-btn')!.addEventListener('click',    () => arController.exit());
    // Attach pinch-to-scale listeners on the AR overlay so they fire reliably
    // on Android Chrome with dom-overlay (document-level listeners can be
    // pre-empted by the WebXR canvas input handler for the transparent area).
    const arOverlayEl = document.getElementById('ar-overlay')!;
    arOverlayEl.addEventListener('touchstart', e => { if (arController.active) arController.handleTouchStart(e); }, { passive: true });
    arOverlayEl.addEventListener('touchmove',  e => { if (arController.active) arController.handleTouchMove(e);  }, { passive: true });
    arOverlayEl.addEventListener('touchend',   e => { if (arController.active) arController.handleTouchEnd(e);   }, { passive: true });

    // ── Save camera view / save settings (admin only) ─────────────────────────
    const saveBtn   = document.getElementById('save-view-btn')!;
    const saveLabel = document.getElementById('save-view-label')!;
    const saveSettingsBtn   = document.getElementById('save-settings-btn')!;
    const saveSettingsLabel = document.getElementById('save-settings-label')!;
    const lodDebugRow = document.getElementById('lod-debug-row');

    fetch('/api/me', { credentials: 'same-origin' })
        .then(r => r.json())
        .then((data: { authenticated: boolean }) => {
            if (data.authenticated) {
                saveBtn.classList.remove('hidden');
                saveSettingsBtn.classList.remove('hidden');
            }
        })
        .catch(() => {});

    function refreshLodDebugVisibility(): void {
        fetch('/api/me', { credentials: 'same-origin' })
            .then(r => r.json())
            .then((data: { authenticated: boolean }) => {
                lodDebugRow?.classList.toggle('hidden', !data.authenticated);
            })
            .catch(() => {});
    }
    refreshLodDebugVisibility();

    window.addEventListener('pageshow', (e) => {
        if (e.persisted) refreshLodDebugVisibility();
    });

    saveBtn.addEventListener('click', async () => {
        if (!currentSlug) return;
        try {
            const res = await fetch(`/api/regions/${currentSlug}/camera`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    position: camera.position.toArray(),
                    target:   controls.target.toArray(),
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            saveLabel.textContent = t('saved');
            saveBtn.classList.add('saved');
            setTimeout(() => { saveLabel.textContent = t('saveView'); saveBtn.classList.remove('saved'); }, 2000);
        } catch (err) {
            console.error('Failed to save camera view:', err);
            saveLabel.textContent = t('errorSave');
            setTimeout(() => { saveLabel.textContent = t('saveView'); }, 2000);
        }
    });

    saveSettingsBtn.addEventListener('click', async () => {
        if (!currentSlug) return;
        try {
            const res = await fetch(`/api/regions/${currentSlug}/settings`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    shading:            currentShading,
                    exaggeration,
                    normalMap:          normalMapEnabled,
                    lodDistance,
                    cityLabelScale:     labelScale,
                    cityLabelOffset:    labelOffset,
                    cityVisRange:       visRange,
                    cityVisHierarchy:   visHierarchy,
                    citySizeHierarchy:  sizeHierarchy,
                    poiVisible:         poisVisible,
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            saveSettingsLabel.textContent = t('saved');
            saveSettingsBtn.classList.add('saved');
            setTimeout(() => { saveSettingsLabel.textContent = t('saveSettings'); saveSettingsBtn.classList.remove('saved'); }, 2000);
        } catch (err) {
            console.error('Failed to save settings:', err);
            saveSettingsLabel.textContent = t('errorSave');
            setTimeout(() => { saveSettingsLabel.textContent = t('saveSettings'); }, 2000);
        }
    });

    // ── Reset settings to the region's admin-saved defaults ───────────────────
    // Lives in the Settings panel, so it only resets what that panel controls
    // (scale, normal map, city label sliders) — not shading/LOD (Map panel) or
    // POI/city visibility (Data panel). Clears the user override keys so these
    // fall back to whatever the admin last saved via "Save settings" for this
    // region (see applyRegionDefaults, which future region switches also honour).
    const resetBtns = document.querySelectorAll<HTMLButtonElement>('#reset-settings-btn, #ar-reset-settings-btn');

    function resetSettingsToDefault(): void {
        localStorage.removeItem(EXAG_KEY);
        localStorage.removeItem(NM_KEY);
        localStorage.removeItem(LABEL_SCALE_KEY);
        localStorage.removeItem(LABEL_OFFSET_KEY);
        localStorage.removeItem(VIS_RANGE_KEY);
        localStorage.removeItem(VIS_HIER_KEY);
        localStorage.removeItem(SIZE_HIER_KEY);

        const s = currentMeta?.settings ?? {};

        setExaggeration(s.exaggeration ?? 1.0, currentTerrain, currentCoords, false);
        if (currentRim) { currentRim.scale.y = exaggeration; updateRimExaggeration(currentRim, exaggeration); }
        if (currentTileSystem) currentTileSystem.group.scale.y = exaggeration;

        applyNormalMapEnabled(s.normalMap ?? true);
        applyNormalMapToggle(currentTerrain);

        applyLabelScale(s.cityLabelScale ?? 1.0, false);
        applyLabelOffset(s.cityLabelOffset ?? 0.5, false);
        applyVisRange(s.cityVisRange ?? 75, false);
        applyVisHierarchy(s.cityVisHierarchy ?? 1.0, false);
        applySizeHierarchy(s.citySizeHierarchy ?? 1.0, false);
    }

    resetBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            resetSettingsToDefault();
            const label = btn.querySelector('span')!;
            const original = t('resetSettings');
            label.textContent = t('resetDone');
            btn.classList.add('saved');
            setTimeout(() => { label.textContent = original; btn.classList.remove('saved'); }, 1500);
        });
    });

    // ── Shading mode ──────────────────────────────────────────────────────────
    function setLodControlsVisible(visible: boolean): void {
        ['lod-controls', 'ar-lod-controls'].forEach(id => {
            document.getElementById(id)?.classList.toggle('hidden', !visible);
        });
    }

    updateShadingUI(currentShading);
    if (currentShading === 'satellite') setLodControlsVisible(true);

    async function setShading(mode: string): Promise<void> {
        if (mode === currentShading && mode !== 'satellite') return;

        if (currentShading === 'satellite') {
            if (currentTileSystem) removeTileSystemFromScene(currentTileSystem, worldRoot);
            if (currentTerrain) currentTerrain.visible = true;
            setLodControlsVisible(false);
        }

        currentShading = mode;
        localStorage.setItem(SHADING_KEY, mode);
        updateShadingUI(mode);

        if (mode === 'satellite') {
            if (currentTerrain) currentTerrain.visible = false;
            const ts = ensureTileSystem();
            if (ts) addTileSystemToScene(ts, worldRoot);
            setLodControlsVisible(true);
            return;
        }

        if (!currentTerrain) return;
        await applyShading(currentTerrain, mode);
    }

    document.getElementById('shading-btns')!.addEventListener('click', e => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.shade-btn');
        if (btn?.dataset.mode) setShading(btn.dataset.mode);
    });

    document.getElementById('ar-shading-btns')!.addEventListener('click', e => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.shade-btn');
        if (btn?.dataset.mode) setShading(btn.dataset.mode);
    });

    // ── Exaggeration controls ─────────────────────────────────────────────────
    function applyExaggeration(val: number): void {
        setExaggeration(val, currentTerrain, currentCoords);
        if (currentRim) {
            currentRim.scale.y = exaggeration;
            updateRimExaggeration(currentRim, exaggeration);
        }
        if (currentTileSystem) currentTileSystem.group.scale.y = exaggeration;
        rescaleCityLabels(exaggeration);
    }
    exagSlider.addEventListener('input', () => applyExaggeration(parseFloat(exagSlider.value)));
    document.getElementById('exag-down')!.addEventListener('click', () => applyExaggeration(exaggeration - EXAG_STEP));
    document.getElementById('exag-up')!.addEventListener('click',   () => applyExaggeration(exaggeration + EXAG_STEP));
    function _toggleNormalMap(): void {
        normalMapEnabled = !normalMapEnabled;
        localStorage.setItem(NM_KEY, String(normalMapEnabled));
        applyNormalMapToggle(currentTerrain);
    }
    normalToggle.addEventListener('click', _toggleNormalMap);
    document.getElementById('ar-normalmap-toggle')?.addEventListener('click', _toggleNormalMap);

    // ── LOD distance control ──────────────────────────────────────────────────
    const lodSlider = document.getElementById('lod-dist-slider') as HTMLInputElement;
    const lodLabel  = document.getElementById('lod-dist-label')!;

    const arLodSlider = document.getElementById('ar-lod-dist-slider') as HTMLInputElement | null;
    const arLodLabel  = document.getElementById('ar-lod-dist-label');

    function setLodDist(v: number, persist = true): void {
        lodDistance = Math.round(v);
        if (persist) localStorage.setItem(LOD_DIST_KEY, String(lodDistance));
        lodSlider.value      = String(lodDistance);
        lodLabel.textContent = String(lodDistance);
        if (arLodSlider) arLodSlider.value = String(lodDistance);
        if (arLodLabel)  arLodLabel.textContent = String(lodDistance);
    }
    setLodDist(lodDistance);
    lodSlider.addEventListener('input', () => setLodDist(parseFloat(lodSlider.value)));
    arLodSlider?.addEventListener('input', () => setLodDist(parseFloat(arLodSlider.value)));

    // ── LOD debug overlay (tints tiles by active LOD; shows live tile counts) ──
    const lodDebugToggle = document.getElementById('lod-debug-toggle') as HTMLInputElement | null;
    const lodDebugCounts = document.getElementById('lod-debug-counts');

    function setLodDebug(enabled: boolean, persist = true): void {
        lodDebugEnabled = enabled;
        if (persist) localStorage.setItem(LOD_DEBUG_KEY, String(enabled));
        if (lodDebugToggle) lodDebugToggle.checked = enabled;
        if (lodDebugCounts) lodDebugCounts.classList.toggle('hidden', !enabled);
        if (currentTileSystem) setLodDebugOverlay(currentTileSystem, enabled);
    }
    setLodDebug(lodDebugEnabled, false);
    lodDebugToggle?.addEventListener('change', () => setLodDebug(lodDebugToggle.checked));

    // ── Region settings presets ───────────────────────────────────────────────
    // For every slider/toggle that has no localStorage override yet, adopt the
    // admin-saved region preset (meta.settings) instead of the hardcoded default.
    // Explicit localStorage values (the user having touched a control) always win.
    function applyRegionDefaults(settings: MetaSettings | undefined): void {
        const s = settings ?? {};
        if (localStorage.getItem(EXAG_KEY) === null) {
            setExaggeration(s.exaggeration ?? 1.0, null, null, false);
        }
        if (localStorage.getItem(NM_KEY) === null) {
            applyNormalMapEnabled(s.normalMap ?? true);
        }
        if (localStorage.getItem(SHADING_KEY) === null) {
            currentShading = sanitizeShading(s.shading);
            updateShadingUI(currentShading);
        }
        if (localStorage.getItem(LOD_DIST_KEY) === null) {
            setLodDist(s.lodDistance ?? 30, false);
        }
        if (localStorage.getItem(LABEL_SCALE_KEY) === null) {
            applyLabelScale(s.cityLabelScale ?? 1.0, false);
        }
        if (localStorage.getItem(LABEL_OFFSET_KEY) === null) {
            applyLabelOffset(s.cityLabelOffset ?? 0.5, false);
        }
        if (localStorage.getItem(VIS_RANGE_KEY) === null) {
            applyVisRange(s.cityVisRange ?? 75, false);
        }
        if (localStorage.getItem(VIS_HIER_KEY) === null) {
            applyVisHierarchy(s.cityVisHierarchy ?? 1.0, false);
        }
        if (localStorage.getItem(SIZE_HIER_KEY) === null) {
            applySizeHierarchy(s.citySizeHierarchy ?? 1.0, false);
        }
        if (!poiVisibleUserSet) {
            applyPoiVisible(s.poiVisible ?? true);
        }
    }

    // ── Region loader ─────────────────────────────────────────────────────────
    async function switchRegion(region: RegionData): Promise<void> {
        const cs    = region.name_cs || region.name;
        const en    = region.name_en || region.name;
        const rname = getLang() === 'cs' ? (cs || en) : (en || cs);
        setLoading(t('loadingRegion', rname));

        if (currentTerrain) {
            worldRoot.remove(currentTerrain);
            disposeTerrain(currentTerrain);
            currentTerrain = null;
        }
        if (currentRim) {
            worldRoot.remove(currentRim);
            disposeRim(currentRim);
            currentRim = null;
        }
        clearMarkers(worldRoot);
        clearCityLabels(worldRoot);

        const { meta, mesh, rim, terrainY, segX, segZ, rawPositions, rawUVs } =
            await loadTerrainData(region.slug);
        currentMeta = meta;
        applyRegionDefaults(meta.settings);
        mesh.scale.y = exaggeration;
        if (rim) { rim.scale.y = exaggeration; updateRimExaggeration(rim, exaggeration); }
        currentTerrain = mesh;
        currentRim     = rim;
        currentSlug    = region.slug;

        // Dispose previous tile system — must remove from worldRoot first.
        if (currentTileSystem) {
            removeTileSystemFromScene(currentTileSystem, worldRoot);
            disposeTileSystem(currentTileSystem);
            currentTileSystem = null;
        }
        const alphaMap = (mesh.material as THREE.MeshStandardMaterial).alphaMap!;
        pendingTileData = { rawPositions, rawUVs, segX, segZ, meta, alphaMap, slug: region.slug };

        // Add mesh/rim to worldRoot; use .visible to control in satellite mode.
        worldRoot.add(mesh);
        if (rim) worldRoot.add(rim);

        applyNormalMapToggle(mesh);

        if (currentShading === 'satellite') {
            mesh.visible = false;
            const ts = ensureTileSystem();
            if (ts) addTileSystemToScene(ts, worldRoot);
        } else {
            await applyShading(mesh, currentShading);
        }

        const coords = new CoordTransform(meta);
        coords._terrainY    = terrainY;
        coords._segX        = segX;
        coords._segZ        = segZ;
        coords.exaggeration = exaggeration;
        currentCoords = coords;

        const effScale = meta.world.elevationScale * exaggeration;

        sun.position.set(meta.world.width * 0.6, effScale * 25, meta.world.height * 0.4);
        const sc = meta.world.width * 0.8;
        Object.assign(sun.shadow.camera, { left: -sc, right: sc, top: sc, bottom: -sc, near: 1, far: 600 });
        sun.shadow.camera.updateProjectionMatrix();

        // The XR device pose drives the camera during an AR session — don't
        // fight it with a desktop framing reset while the model is placed in AR.
        if (!arController.active) {
            if (meta.defaultCamera) {
                const { position: p, target: tgt } = meta.defaultCamera;
                camera.position.set(p[0], p[1], p[2]);
                controls.target.set(tgt[0], tgt[1], tgt[2]);
            } else {
                controls.target.set(0, effScale * 0.2, 0);
                camera.position.set(0, effScale * 1.8, meta.world.height * 0.9);
            }
            camera.lookAt(controls.target);
            controls.minDistance = 0.5;
            controls.maxDistance = meta.world.width * 2.5;
            controls.update();
        }

        scene.fog = new THREE.Fog(0x7aaecc, meta.world.width * 1.5, meta.world.width * 3);

        await loadAndPlacePOIs(region.slug, coords, worldRoot);
        await loadAndPlaceCities(region.slug, coords, worldRoot);

        hideLoading();
    }

    setupInteraction(
        () => getMarkers(),
        camera,
        renderer,
    );

    // ── Panel icon buttons (Map / Data / Settings) ───────────────────────────
    // Generic group: wires N icon buttons to N floating panels, closing the
    // others whenever one opens (and on outside click). Desktop and AR each
    // get their own independent group/instance since they're never visible
    // at the same time (AR uses the dom-overlay root; desktop UI is hidden
    // behind it during an XR session).
    function createPanelGroup(pairs: [HTMLElement, HTMLElement][], onOpen?: (panel: HTMLElement) => void) {
        const btns   = pairs.map(([b]) => b);
        const panels = pairs.map(([, p]) => p);

        function closeAll(): void {
            panels.forEach(p => p.classList.add('hidden'));
            btns.forEach(b => b.setAttribute('aria-expanded', 'false'));
        }

        pairs.forEach(([btn, panel]) => {
            btn.addEventListener('click', e => {
                e.stopPropagation();
                const isOpen = !panel.classList.contains('hidden');
                closeAll();
                if (!isOpen) {
                    panel.classList.remove('hidden');
                    btn.setAttribute('aria-expanded', 'true');
                    onOpen?.(panel);
                }
            });
            panel.addEventListener('click', e => e.stopPropagation());
        });

        document.addEventListener('click', closeAll);

        return { closeAll };
    }

    createPanelGroup([
        [document.getElementById('btn-map')!,      document.getElementById('panel-map')!],
        [document.getElementById('btn-data')!,     document.getElementById('panel-data')!],
        [document.getElementById('btn-settings')!, document.getElementById('panel-settings')!],
    ]);

    const arPanelGroup = createPanelGroup([
        [document.getElementById('ar-btn-map')!,      document.getElementById('ar-panel-map')!],
        [document.getElementById('ar-btn-data')!,     document.getElementById('ar-panel-data')!],
        [document.getElementById('ar-btn-settings')!, document.getElementById('ar-panel-settings')!],
    ], panel => {
        if (panel.id === 'ar-panel-settings') syncArSettingsPanel();
    });

    // ── Help modal (controls explainer) ──────────────────────────────────────
    const helpModal    = document.getElementById('help-modal')!;
    const helpBackdrop = document.getElementById('help-backdrop')!;

    function showHelp(): void {
        helpModal.classList.remove('hidden');
        helpBackdrop.classList.remove('hidden');
    }
    function hideHelp(): void {
        helpModal.classList.add('hidden');
        helpBackdrop.classList.add('hidden');
    }
    document.getElementById('btn-help')!.addEventListener('click', showHelp);
    document.getElementById('help-close')!.addEventListener('click', hideHelp);
    helpBackdrop.addEventListener('click', hideHelp);
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !helpModal.classList.contains('hidden')) hideHelp();
    });
    arController.setClosePanelsCallback(arPanelGroup.closeAll);

    // ── POI visibility + category filter ─────────────────────────────────────
    const poiToggle     = document.getElementById('poi-toggle')!;
    const arPoiToggle    = document.getElementById('ar-poi-toggle')!;
    const poiFilterRows  = document.querySelectorAll<HTMLElement>('#poi-filter-row, #ar-poi-filter-row');
    let poisVisible = true;
    let poiVisibleUserSet = false;

    function applyPoiVisible(value: boolean): void {
        poisVisible = value;
        const val = String(poisVisible);
        const label = poisVisible ? t('on') : t('off');
        poiToggle.setAttribute('aria-checked', val);
        poiToggle.textContent = label;
        arPoiToggle.setAttribute('aria-checked', val);
        arPoiToggle.textContent = label;
        poiFilterRows.forEach(row => row.classList.toggle('hidden', !poisVisible));
        setPoisVisible(poisVisible);
    }

    function _togglePois(): void {
        poiVisibleUserSet = true;
        applyPoiVisible(!poisVisible);
    }
    poiToggle.addEventListener('click', _togglePois);
    arPoiToggle.addEventListener('click', _togglePois);

    function handlePoiFilterClick(e: Event): void {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.poi-filter');
        if (!btn?.dataset.cat) return;
        document.querySelectorAll<HTMLButtonElement>('.poi-filter').forEach(b => {
            b.classList.toggle('active', b.dataset.cat === btn.dataset.cat);
        });
        filterByCategory(btn.dataset.cat);
    }
    poiFilterRows.forEach(row => row.addEventListener('click', handlePoiFilterClick));

    // Category filter buttons (beyond the static "All" button) are built from
    // the shared category list fetched in init(), so a new category shows up
    // here without touching this file.
    function renderPoiFilterButtons(): void {
        poiFilterRows.forEach(row => {
            row.querySelectorAll('.poi-filter[data-dynamic]').forEach(b => b.remove());
            getCategories().filter(c => c.filterable).forEach(c => {
                const btn = document.createElement('button');
                btn.className = 'poi-filter';
                btn.dataset.cat = c.key;
                btn.dataset.dynamic = '1';
                row.appendChild(btn);
            });
        });
        updatePoiFilterButtonLabels();
    }

    function updatePoiFilterButtonLabels(): void {
        const lang = getLang();
        document.querySelectorAll<HTMLButtonElement>('.poi-filter[data-dynamic]').forEach(btn => {
            const c = getCategory(btn.dataset.cat);
            btn.textContent = lang === 'cs' ? c.label_cs : c.label_en;
        });
    }
    onLangChange(() => updatePoiFilterButtonLabels());

    // City name sprites are baked into canvas textures at load time, so a
    // language switch needs a full rebuild to pick up name_en vs name.
    onLangChange(() => {
        if (currentSlug && currentCoords) {
            loadAndPlaceCities(currentSlug, currentCoords, worldRoot);
        }
    });

    // ── City label visibility + size filter ───────────────────────────────────
    const cityToggle     = document.getElementById('city-toggle')!;
    const arCityToggle    = document.getElementById('ar-city-toggle')!;
    const cityFilterRows  = document.querySelectorAll<HTMLElement>('#city-filter-row, #ar-city-filter-row');
    let citiesVisible = true;

    function _toggleCities(): void {
        citiesVisible = !citiesVisible;
        const val = String(citiesVisible);
        const label = citiesVisible ? t('on') : t('off');
        cityToggle.setAttribute('aria-checked', val);
        cityToggle.textContent = label;
        arCityToggle.setAttribute('aria-checked', val);
        arCityToggle.textContent = label;
        cityFilterRows.forEach(row => row.classList.toggle('hidden', !citiesVisible));
        setCitiesVisible(citiesVisible);
    }
    cityToggle.addEventListener('click', _toggleCities);
    arCityToggle.addEventListener('click', _toggleCities);

    function handleCityFilterClick(e: Event): void {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.city-filter');
        if (!btn?.dataset.size) return;
        const nowActive = toggleCitySize(btn.dataset.size);
        document.querySelectorAll<HTMLButtonElement>('.city-filter').forEach(b => {
            if (b.dataset.size === btn.dataset.size) b.classList.toggle('active', nowActive);
        });
    }
    cityFilterRows.forEach(row => row.addEventListener('click', handleCityFilterClick));

    // ── City label size + offset sliders ─────────────────────────────────────
    // Each desktop slider has an `ar-` prefixed twin with the same range;
    // apply* functions write to both so they stay in sync regardless of
    // which one the user drags.
    const labelSlider    = document.getElementById('city-label-slider')  as HTMLInputElement;
    const labelValEl     = document.getElementById('city-label-val')!;
    const arLabelSlider  = document.getElementById('ar-city-label-slider') as HTMLInputElement | null;
    const arLabelValEl   = document.getElementById('ar-city-label-val');
    const offsetSlider   = document.getElementById('city-offset-slider') as HTMLInputElement;
    const offsetValEl    = document.getElementById('city-offset-val')!;
    const arOffsetSlider = document.getElementById('ar-city-offset-slider') as HTMLInputElement | null;
    const arOffsetValEl  = document.getElementById('ar-city-offset-val');

    function applyLabelScale(v: number, persist = true): void {
        labelScale = Math.round(v * 20) / 20;   // snap to 0.05 steps
        if (persist) localStorage.setItem(LABEL_SCALE_KEY, String(labelScale));
        const text = labelScale.toFixed(2) + '×';
        labelSlider.value      = String(labelScale);
        labelValEl.textContent = text;
        if (arLabelSlider) arLabelSlider.value = String(labelScale);
        if (arLabelValEl)  arLabelValEl.textContent = text;
    }
    function applyLabelOffset(v: number, persist = true): void {
        labelOffset = Math.round(v * 10) / 10;
        const text = labelOffset.toFixed(1);
        offsetSlider.value      = String(labelOffset);
        offsetValEl.textContent = text;
        if (arOffsetSlider) arOffsetSlider.value = String(labelOffset);
        if (arOffsetValEl)  arOffsetValEl.textContent = text;
        setLabelOffset(labelOffset, persist);
        rescaleCityLabels(exaggeration);
    }

    const visRangeSlider   = document.getElementById('city-vis-range-slider') as HTMLInputElement;
    const visRangeValEl    = document.getElementById('city-vis-range-val')!;
    const arVisRangeSlider = document.getElementById('ar-city-vis-range-slider') as HTMLInputElement | null;
    const arVisRangeValEl  = document.getElementById('ar-city-vis-range-val');
    const visHierSlider    = document.getElementById('city-vis-hier-slider')  as HTMLInputElement;
    const visHierValEl     = document.getElementById('city-vis-hier-val')!;
    const arVisHierSlider  = document.getElementById('ar-city-vis-hier-slider') as HTMLInputElement | null;
    const arVisHierValEl   = document.getElementById('ar-city-vis-hier-val');
    const sizeHierSlider   = document.getElementById('city-size-hier-slider') as HTMLInputElement;
    const sizeHierValEl    = document.getElementById('city-size-hier-val')!;
    const arSizeHierSlider = document.getElementById('ar-city-size-hier-slider') as HTMLInputElement | null;
    const arSizeHierValEl  = document.getElementById('ar-city-size-hier-val');

    function applyVisRange(v: number, persist = true): void {
        visRange = Math.round(v);
        setVisRange(visRange, persist);
        const text = String(visRange);
        visRangeSlider.value      = text;
        visRangeValEl.textContent = text;
        if (arVisRangeSlider) arVisRangeSlider.value = text;
        if (arVisRangeValEl)  arVisRangeValEl.textContent = text;
    }
    function applyVisHierarchy(v: number, persist = true): void {
        visHierarchy = Math.round(v * 20) / 20;
        setVisHierarchy(visHierarchy, persist);
        const text = visHierarchy.toFixed(2);
        visHierSlider.value      = String(visHierarchy);
        visHierValEl.textContent = text;
        if (arVisHierSlider) arVisHierSlider.value = String(visHierarchy);
        if (arVisHierValEl)  arVisHierValEl.textContent = text;
    }
    function applySizeHierarchy(v: number, persist = true): void {
        sizeHierarchy = Math.round(v * 20) / 20;
        setSizeHierarchy(sizeHierarchy, persist);
        const text = sizeHierarchy.toFixed(2);
        sizeHierSlider.value      = String(sizeHierarchy);
        sizeHierValEl.textContent = text;
        if (arSizeHierSlider) arSizeHierSlider.value = String(sizeHierarchy);
        if (arSizeHierValEl)  arSizeHierValEl.textContent = text;
    }

    applyLabelScale(labelScale);
    applyLabelOffset(labelOffset);
    applyVisRange(visRange);
    applyVisHierarchy(visHierarchy);
    applySizeHierarchy(sizeHierarchy);

    labelSlider.addEventListener('input',   () => applyLabelScale(parseFloat(labelSlider.value)));
    offsetSlider.addEventListener('input',  () => applyLabelOffset(parseFloat(offsetSlider.value)));
    visRangeSlider.addEventListener('input', () => applyVisRange(parseFloat(visRangeSlider.value)));
    visHierSlider.addEventListener('input',  () => applyVisHierarchy(parseFloat(visHierSlider.value)));
    sizeHierSlider.addEventListener('input', () => applySizeHierarchy(parseFloat(sizeHierSlider.value)));

    arLabelSlider?.addEventListener('input',    () => applyLabelScale(parseFloat(arLabelSlider.value)));
    arOffsetSlider?.addEventListener('input',   () => applyLabelOffset(parseFloat(arOffsetSlider.value)));
    arVisRangeSlider?.addEventListener('input', () => applyVisRange(parseFloat(arVisRangeSlider.value)));
    arVisHierSlider?.addEventListener('input',  () => applyVisHierarchy(parseFloat(arVisHierSlider.value)));
    arSizeHierSlider?.addEventListener('input', () => applySizeHierarchy(parseFloat(arSizeHierSlider.value)));

    // Re-sync the AR settings panel's slider positions/labels whenever it's opened
    // (values may have changed via the desktop panel while AR was in the background).
    function syncArSettingsPanel(): void {
        applyLabelScale(labelScale);
        applyLabelOffset(labelOffset);
        applyVisRange(visRange);
        applyVisHierarchy(visHierarchy);
        applySizeHierarchy(sizeHierarchy);
        const s = document.getElementById('ar-exag-slider') as HTMLInputElement | null;
        if (s) s.value = String(exaggeration);
        const arExagLabel = document.getElementById('ar-exag-ar-label');
        if (arExagLabel) arExagLabel.textContent = exaggeration.toFixed(2).replace(/\.?0+$/, '') + '×';
    }

    await loadCategories();
    renderPoiFilterButtons();

    initRegionBars(['region-bar', 'ar-region-bar']);
    const firstRegion = await loadRegions(switchRegion);
    if (!firstRegion) {
        loadingMsg.textContent = t('noRegions');
        return;
    }
    await switchRegion(firstRegion);

    window.addEventListener('resize', () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    });

    function animate(_: number, frame: unknown): void {
        if (frame) arController.onFrame(frame as Parameters<ARController['onFrame']>[0]);
        applyKeyMovement();
        controls.update();
        if (currentShading === 'satellite' && currentTileSystem) {
            const counts = updateTileLOD(currentTileSystem, camera, lodDistance, worldRoot);
            if (lodDebugEnabled && lodDebugCounts) {
                lodDebugCounts.textContent = `${t('lodNear')}: ${counts.lod0}  ${t('lodFar')}: ${counts.lod1}`;
            }
        }
        updateCityLOD(camera, labelScale, worldRoot);
        renderer.render(scene, camera);
    }
    renderer.setAnimationLoop(animate);
    arController.setAnimateCallback(animate);

    (document.getElementById('ar-exag-slider') as HTMLInputElement | null)
        ?.addEventListener('input', e => {
            applyExaggeration(parseFloat((e.target as HTMLInputElement).value));
        });
    arController.initSettingsUI();

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            refreshLodDebugVisibility();
            if (!renderer.xr.isPresenting) {
                // Always resume the render loop so the desktop view is
                // interactive again, even if the XR session's 'end' event
                // hasn't fired yet — never leave the user on a frozen page.
                renderer.setAnimationLoop(animate);
                if (arController.forcedEnd && currentTerrain) {
                    // WebXR requires requestSession() to be called from a
                    // real user gesture (tap), so we cannot silently restart
                    // AR here — that would throw a SecurityError and leave
                    // the user stuck. Instead, prompt them to tap the AR
                    // button again; one tap and they're straight back in.
                    arBtn.classList.add('ar-resume-pulse');
                }
            }
        }
    });
}

init().catch(err => {
    console.error('Init failed:', err);
    loadingMsg.innerHTML = `<span style="color:#f88">Failed to initialise: ${(err as Error).message}</span>`;
});
